/*
 * Sale Banner — behavior for sections/sale-banner.liquid
 * 1:1 port of the React SaleBanner logic: countdown with split-flap animation,
 * rotating headline, autoplay carousel, keyboard navigation, active-dot tracking.
 * Works with multiple instances on the same page and re-inits in the Theme Editor.
 */
(function () {
  "use strict";

  function pad(n) {
    return String(n).padStart(2, "0");
  }

  function initSaleBanner(root) {
    if (!root || root.dataset.sbInit === "1") return;
    root.dataset.sbInit = "1";

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* ---------- Countdown ---------- */
    var timerEl = root.querySelector("[data-sb-timer]");
    if (timerEl) {
      var hoursStart = parseInt(root.dataset.countdownHours || "0", 10);
      var minutesStart = parseInt(root.dataset.countdownMinutes || "0", 10);
      var secondsStart = parseInt(root.dataset.countdownSeconds || "0", 10);
      var remaining = hoursStart * 3600 + minutesStart * 60 + secondsStart;

      var units = {
        hours: root.querySelector('[data-unit="hours"]'),
        minutes: root.querySelector('[data-unit="minutes"]'),
        seconds: root.querySelector('[data-unit="seconds"]'),
      };

      var setUnit = function (el, value) {
        if (!el) return;
        var str = pad(value);
        if (el.textContent === str) return;
        el.textContent = str;
        if (!reduceMotion) {
          el.classList.remove("sb-flap");
          // force reflow so the animation restarts
          void el.offsetWidth;
          el.classList.add("sb-flap");
        }
      };

      var renderTimer = function () {
        var h = Math.floor(remaining / 3600);
        var m = Math.floor((remaining % 3600) / 60);
        var s = remaining % 60;
        setUnit(units.hours, h);
        setUnit(units.minutes, m);
        setUnit(units.seconds, s);
        timerEl.setAttribute(
          "aria-label",
          "Sale ends in " + h + " hours " + m + " minutes " + s + " seconds"
        );
      };

      renderTimer();
      setInterval(function () {
        if (remaining > 0) remaining -= 1;
        renderTimer();
      }, 1000);
    }

    /* ---------- Rotating headline ---------- */
    var headlineText = root.querySelector("[data-sb-headline-text]");
    var headlineLink = root.querySelector("[data-sb-headline]");
    var headlineJson = root.querySelector("[data-sb-headlines]");
    if (headlineText && headlineJson) {
      var headlines = [];
      try {
        headlines = String(JSON.parse(headlineJson.textContent))
          .split("||")
          .map(function (s) { return s.trim(); })
          .filter(Boolean);
      } catch (e) {
        headlines = [headlineText.textContent.trim()];
      }
      if (headlines.length > 1 && !reduceMotion) {
        var hi = 0;
        var headlinePaused = false;
        if (headlineLink) {
          headlineLink.addEventListener("mouseenter", function () { headlinePaused = true; });
          headlineLink.addEventListener("mouseleave", function () { headlinePaused = false; });
          headlineLink.addEventListener("focus", function () { headlinePaused = true; });
          headlineLink.addEventListener("blur", function () { headlinePaused = false; });
        }
        var tick = function () {
          if (!headlinePaused && document.visibilityState === "visible") {
            hi = (hi + 1) % headlines.length;
            headlineText.textContent = headlines[hi];
            if (headlineLink) headlineLink.setAttribute("aria-label", headlines[hi]);
            headlineText.style.animation = "none";
            void headlineText.offsetWidth;
            headlineText.style.animation = "";
          }
          setTimeout(tick, 3200);
        };
        setTimeout(tick, 3200);
      }
    }

    /* ---------- Carousel ---------- */
    var track = root.querySelector("[data-sb-track]");
    var carousel = root.querySelector("[data-sb-carousel]");
    if (track && carousel) {
      var cards = Array.prototype.slice.call(track.querySelectorAll(".sb-item"));
      var dotsWrap = root.querySelector("[data-sb-dots]");
      var announce = root.querySelector("[data-sb-announce]");
      var activeIndex = 0;
      var paused = false;

      // Build dots
      if (dotsWrap) {
        cards.forEach(function () {
          var d = document.createElement("span");
          d.className = "sb-dot";
          dotsWrap.appendChild(d);
        });
      }
      var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.children) : [];

      var setActive = function (i) {
        activeIndex = i;
        cards.forEach(function (c, idx) {
          var a = c.querySelector(".sb-card");
          if (a) a.classList.toggle("is-active", idx === i);
          if (idx === i) a && a.setAttribute("aria-current", "true");
          else a && a.removeAttribute("aria-current");
        });
        dots.forEach(function (d, idx) { d.classList.toggle("is-active", idx === i); });
      };

      var goToIndex = function (index, focus) {
        var clamped = Math.max(0, Math.min(index, cards.length - 1));
        var li = cards[clamped];
        if (li) li.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        setActive(clamped);
        if (announce) {
          announce.textContent =
            (li.querySelector(".sb-brand") ? li.querySelector(".sb-brand").textContent : "") +
            ", product " + (clamped + 1) + " of " + cards.length + " ";
        }
        if (focus) {
          var a = li && li.querySelector(".sb-card");
          if (a) a.focus({ preventScroll: true });
        }
      };

      // Track active card while scrolling
      var raf = 0;
      var onScroll = function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          if (!cards.length) return;
          var mid = track.scrollLeft + track.clientWidth / 2;
          var closest = 0, min = Infinity;
          cards.forEach(function (c, i) {
            var center = c.offsetLeft + c.offsetWidth / 2;
            var dist = Math.abs(center - mid);
            if (dist < min) { min = dist; closest = i; }
          });
          setActive(closest);
        });
      };
      track.addEventListener("scroll", onScroll, { passive: true });

      // Arrows
      var prevBtn = root.querySelector("[data-sb-prev]");
      var nextBtn = root.querySelector("[data-sb-next]");
      if (prevBtn) prevBtn.addEventListener("click", function () { goToIndex(activeIndex - 1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { goToIndex(activeIndex + 1); });

      // Keyboard
      track.addEventListener("keydown", function (e) {
        var focused = cards.findIndex(function (c) {
          return c.querySelector(".sb-card") === document.activeElement;
        });
        var base = focused >= 0 ? focused : activeIndex;
        switch (e.key) {
          case "ArrowRight": e.preventDefault(); goToIndex(base + 1, true); break;
          case "ArrowLeft": e.preventDefault(); goToIndex(base - 1, true); break;
          case "Home": e.preventDefault(); goToIndex(0, true); break;
          case "End": e.preventDefault(); goToIndex(cards.length - 1, true); break;
        }
      });

      // Pause on interaction
      ["mouseenter", "focusin", "touchstart"].forEach(function (ev) {
        track.addEventListener(ev, function () { paused = true; }, { passive: true });
      });
      ["mouseleave", "focusout", "touchend"].forEach(function (ev) {
        track.addEventListener(ev, function () { paused = false; }, { passive: true });
      });

      // Autoplay
      if (!reduceMotion && cards.length > 1) {
        setInterval(function () {
          if (paused) return;
          var gap = 12;
          var step = cards[0] ? cards[0].offsetWidth + gap : track.clientWidth / 3;
          var maxScroll = track.scrollWidth - track.clientWidth;
          if (track.scrollLeft >= maxScroll - 4) {
            track.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            track.scrollBy({ left: step, behavior: "smooth" });
          }
        }, 2200);
      }

      setActive(0);
    }
  }

  function initAll() {
    document.querySelectorAll("[data-sale-banner]").forEach(initSaleBanner);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor support
  document.addEventListener("shopify:section:load", function (e) {
    var el = e.target.querySelector("[data-sale-banner]");
    if (el) { el.dataset.sbInit = ""; initSaleBanner(el); }
  });
})();